import { HasProduct, ProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { ChangeEvent, useContext } from "react";
import styled, { css } from "styled-components";

const QuantityWrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    width: 100px;
`;

const QuantityButton = styled(Clickable)`
    position: absolute;
    z-index: 1;
    top: calc(50% - 8px);
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    span[class^="IconWrapper-sc-"] {
        color: ${({ theme }) => theme.colors.primary.main};
    }
`;

const MinusButton = styled(QuantityButton)`
    left: 10px;
`;

const PlusButton = styled(QuantityButton)`
    right: 10px;
`;

const quantityFieldStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formField: css`
            margin-bottom: 0;
            flex: 1;
        `,
        formInputWrapper: css`

            margin-top: 0 !important;

            input[type="number"] {
                text-align: center;
                padding: 0 !important;
                
                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                
                &:focus {
                    border-left: none;
                    border-right: none;
                }
            }
        `,
        inputSelect: css`
            -moz-appearance: textfield;
        `,
    },
    labelProps: {
        css: css`
            display: none;
        `,
    },
};

/**
 * Quantity input component for variant products in grid view.
 * Features custom +/- buttons on the sides for better UX.
 */
const VariantQuantityOrdered: React.FC<HasProduct> = ({ product }) => {
    const context = useContext(ProductContext);
    const qtyOrdered = context?.productInfo?.qtyOrdered || 0;
    const onQtyOrderedChanged = context?.onQtyOrderedChanged;

    const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.currentTarget.value, 10);
        if (!isNaN(value) && value >= 0 && onQtyOrderedChanged) {
            onQtyOrderedChanged(value);
        }
    };

    const handleIncrement = () => {
        if (onQtyOrderedChanged) {
            onQtyOrderedChanged(qtyOrdered + 1);
        }
    };

    const handleDecrement = () => {
        if (qtyOrdered > 0 && onQtyOrderedChanged) {
            onQtyOrderedChanged(qtyOrdered - 1);
        }
    };

    return (
        <QuantityWrapper>
            <MinusButton 
                onClick={handleDecrement} 
                disabled={qtyOrdered <= 0}
                data-test-selector="decrementQuantity"
            >
                <Icon src={Minus} size={16} />
            </MinusButton>
            <TextField
                {...quantityFieldStyles}
                label={translate("QTY")}
                value={qtyOrdered}
                type="number"
                min={0}
                onChange={handleQuantityChange}
                data-test-selector="quantityInput"
            />
            <PlusButton 
                onClick={handleIncrement}
                data-test-selector="incrementQuantity"
            >
                <Icon src={Plus} size={16} />
            </PlusButton>
        </QuantityWrapper>
    );
};

export default withProduct(VariantQuantityOrdered);